import React, { useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import { useSelector } from 'react-redux';
import { TitleSmall500, TitleXSmall500, BodyMedium400, BodyMedium500 } from '@nebula/global-styles';
import { TextLinkInline } from '@nebula/text-link';
import { useMediaQuery } from '@nebula/hooks';
import { BaseLozengeBadge, badgeSizeEnum } from '@nebula/badge';
import { InformationAlert } from '@nebula/alert';
import { openInternalBrowser } from '@ecs-uk/web-common-mobile-view-helpers';

import { getEnv } from '../../Context/env';
import AccountDetailsLink, { getFactSheetUrl } from '../../MySubscription/shared/AccountDetailsLink';
import { TrialDuration } from '../constants.ts';
import useBillingChangeInfo from '../hooks/useBillingChangeInfo';
import { PaymentContext } from '../../PaymentOrchestrator/PaymentContextProvider';
import { products } from '../../../constants';
import { useGetAllProductsQuery, useGetEntitlementsQuery } from '../../../services/entitlementsApi';
import PricingTermCard from './PricingTermCard';
import AnnualPricingOptions from './AnnualPricingOptions';
import {
  Summary,
  Product,
  Divider,
  InfoAlertWrapper,
  TermsContainer,
  PlanPriceContainer,
  LinkContainer,
  ViewLabel,
  SubscriptionDetails,
} from './index.styles';

dayjs.extend(advancedFormat);

const formatPrice = value => {
  if (!Number.isFinite(Number(value))) {
    return value;
  }
  return Number(value).toFixed(2).replace(/\.00$/, '');
};

const handleClick = (e, url, isWebview) => {
  if (isWebview) {
    e.preventDefault();
    openInternalBrowser(new URL(url));
  }
};

const TextLink = ({ url, text, isWebview }) => (
  <TextLinkInline href={url} target="_blank" onClick={e => handleClick(e, url, isWebview)}>
    {text}
  </TextLinkInline>
);

TextLink.propTypes = {
  url: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  isWebview: PropTypes.bool,
};

const PaymentReview = () => {
  const theme = useContext(ThemeContext);
  const env = getEnv();
  const {
    resolvedIsTrial,
    resolvedUpgradeOffer,
    resolvedCurrentSubscription,
    isFlexFlow,
    isWebview,
    isBillingPeriodChange,
    targetBillingPeriod,
    setSelectedPlanOverride,
  } = useContext(PaymentContext);
  const [selectedPricing, setSelectedPricing] = useState(null);

  const { billingStartDate, periodicity, price, remainingTimeUntilDate, isProRataMovement, proRataPrice } =
    useBillingChangeInfo({
      upgradeOffer: resolvedUpgradeOffer,
      isTrial: resolvedIsTrial,
    });
  const reduxCurrentSubscription = useSelector(state => state.subscriptions.current);
  const currentSubscription = isFlexFlow ? resolvedCurrentSubscription : reduxCurrentSubscription;
  const formattedStartDate = billingStartDate.format('DD MMMM YYYY');
  const formattedRemainingTimeDate = remainingTimeUntilDate?.format('DD MMMM YYYY');

  const isDesktop = useMediaQuery(theme.media.smallDesktopMin);
  const ProductLabel = isDesktop ? TitleSmall500 : TitleXSmall500;
  const isMovingToCreditExper = resolvedUpgradeOffer?.family === products.credit_expert;
  const isMovingToIdentity = resolvedUpgradeOffer?.family === products.identity;
  const isIdentityCustomer = currentSubscription?.family === products.identity;
  const isCreditExpertCustomer = currentSubscription?.family === products.credit_expert;
  const isCreditExpertToIdentity = isCreditExpertCustomer && isMovingToIdentity;
  const isFlexCustomer = Boolean(currentSubscription?.planCode);
  const monthlyPrice = Number(price);

  const { data: productsData } = useGetAllProductsQuery(undefined, {
    skip: !isBillingPeriodChange,
  });
  const { data: entitlements } = useGetEntitlementsQuery(undefined, {
    skip: !isBillingPeriodChange || !isFlexFlow,
  });

  // The plan being moved to when changing billing period - carries the real price and discount from the API
  const billingPeriodPlan = useMemo(() => {
    if (!isBillingPeriodChange) {
      return null;
    }

    return (productsData?.eligiblePlans ?? []).find(plan => plan?.planCode === resolvedUpgradeOffer?.planCode) ?? null;
  }, [isBillingPeriodChange, productsData, resolvedUpgradeOffer?.planCode]);

  const summaryPeriodicity = selectedPricing?.summaryLabel ?? `${periodicity} Subscription`;
  const summaryPrice = formatPrice(selectedPricing?.summaryPrice ?? price);

  let termsAgreementIntro = 'When you upgrade, you agree to our';
  if (isCreditExpertToIdentity) {
    termsAgreementIntro = 'When you switch subscription, you agree to our';
  } else if (resolvedIsTrial) {
    termsAgreementIntro = `When you start your ${TrialDuration} day free trial, you agree to our`;
  }

  const termsStatement = (
    <React.Fragment>
      {termsAgreementIntro}{' '}
      <TextLink
        url={`${env.REACT_APP_EXPERIAN_URL}/consumer/terms.html`}
        text="Terms & Conditions"
        isWebview={isWebview}
      />
      . We collect and use your data in line with our{' '}
      <TextLink
        url={`${env.REACT_APP_EXPERIAN_URL}/consumer/privacy.html`}
        text="Privacy Policy"
        isWebview={isWebview}
      />
      . Cancel anytime.
    </React.Fragment>
  );

  if (isBillingPeriodChange) {
    const isTargetAnnual = targetBillingPeriod === 'annual';
    const targetPrice = Number(billingPeriodPlan?.price ?? price);
    const originalPrice = Number(billingPeriodPlan?.originalPrice ?? targetPrice);
    const discountPercentage = billingPeriodPlan?.discountPercentage;
    const formattedPrice = formatPrice(targetPrice);
    // Both billing-period changes use the customer's next bill date. Today plus a month or a year
    // shifts that day, so 1 November becomes 2 November when the page is opened on the 2nd.
    const flexNextBillRaw = entitlements?.plan?.nextBillDate ?? entitlements?.plans?.[0]?.nextBillDate;
    const flexNextBillDate = flexNextBillRaw ? dayjs(flexNextBillRaw) : null;
    const useExistingNextBill = isFlexFlow && flexNextBillDate?.isValid();
    const billAnchor = useExistingNextBill ? flexNextBillDate : billingStartDate;
    const billingDay = billAnchor.format('D MMMM');
    const nextPaymentDate = isTargetAnnual ? billingStartDate.add(1, 'year') : billingStartDate.add(1, 'month');
    const formattedNextPaymentDate = useExistingNextBill
      ? flexNextBillDate.format('D MMMM YYYY')
      : nextPaymentDate.format('D MMMM YYYY');

    const cardProps = isTargetAnnual
      ? {
          id: 'id-annual',
          termText: 'Annual',
          priceText: `£${formatPrice(originalPrice)}`,
          frequencyText: '/year',
          billingText: `Renews automatically on ${billingDay} every year`,
          ...(discountPercentage ? { discountBadgeText: `Save ${discountPercentage}%` } : {}),
          discountedPriceText: `£${formattedPrice}`,
          monthlyEquivalentText: `£${formatPrice(targetPrice / 12)}/month`,
        }
      : {
          id: 'id-monthly',
          termText: 'Monthly',
          priceText: `£${formattedPrice}`,
          frequencyText: '/month',
          billingText: `Billed on the ${billAnchor.format('Do')} of every month`,
        };

    return (
      <React.Fragment>
        <PricingTermCard {...cardProps} hideRadio onSelect={() => {}} />
        <PlanPriceContainer>
          <ProductLabel>Next payment on {formattedNextPaymentDate}</ProductLabel>
          <ProductLabel>£{formattedPrice}</ProductLabel>
        </PlanPriceContainer>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {/* if Flex ID-Free customer tries to upsell to CE notify his free trail ends */}
      {currentSubscription?.freeTrial && isFlexCustomer && isIdentityCustomer && isMovingToCreditExper && (
        <InfoAlertWrapper>
          <InformationAlert
            message="Your free trial of Identity Plus will end if you switch to CreditExpert."
            showIcon
          />
        </InfoAlertWrapper>
      )}
      <AnnualPricingOptions
        billingStartDate={billingStartDate}
        currentSubscription={currentSubscription}
        monthlyPrice={monthlyPrice}
        resolvedUpgradeOffer={resolvedUpgradeOffer}
        setSelectedPlanOverride={setSelectedPlanOverride}
        onPricingChange={setSelectedPricing}
      />

      <Summary>
        <div>
          {resolvedIsTrial && (
            <React.Fragment>
              <BodyMedium400>Starting {remainingTimeUntilDate ? formattedRemainingTimeDate : 'today'}</BodyMedium400>
              <Product>
                <ProductLabel>{TrialDuration} day trial</ProductLabel>
                <BaseLozengeBadge
                  badgeSize={badgeSizeEnum.medium}
                  backgroundColour={theme.color.ui.white}
                  iconColour={theme.color.ui.white}
                  text="No charge"
                  textColour={theme.color.ui.grey[90]}
                />
              </Product>
              <Divider />
            </React.Fragment>
          )}
          {isProRataMovement && (
            <React.Fragment>
              <Product>
                <ProductLabel>Today’s payment</ProductLabel>
                <ProductLabel>£{proRataPrice}</ProductLabel>
              </Product>
              <Divider />
            </React.Fragment>
          )}
          <BodyMedium400>Starting on {formattedStartDate}</BodyMedium400>
          <Product>
            {summaryPeriodicity && <ProductLabel>{summaryPeriodicity}</ProductLabel>}
            <ProductLabel>£{summaryPrice}</ProductLabel>
          </Product>
        </div>

        <SubscriptionDetails>
          <BodyMedium500>Subscription details</BodyMedium500>
          <TermsContainer>
            <BodyMedium400>{termsStatement}</BodyMedium400>
          </TermsContainer>
          {resolvedUpgradeOffer?.family && (
            <LinkContainer>
              <ViewLabel>View</ViewLabel>
              <AccountDetailsLink
                href={getFactSheetUrl(resolvedUpgradeOffer.family)}
                underlineAlways
                isWebview={isWebview}
              />
            </LinkContainer>
          )}
        </SubscriptionDetails>
      </Summary>
    </React.Fragment>
  );
};

export default PaymentReview;
